import React, { useState } from 'react';
import { X, Calendar, Users, Clock, AlertTriangle, Search, UserPlus } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useEntity } from '../../hooks/useEntity';
import { createProject, updateProject } from '../../lib/supabase/projects';
import { getContacts } from '../../lib/supabase/contacts';
import type { Project } from '../../types/project';
import type { Contact } from '../../types/contacts';

interface ProjectFormProps {
  project?: Project;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ProjectForm({ project, onClose, onSuccess }: ProjectFormProps) {
  const { user } = useAuthStore();
  const { entity } = useEntity();
  const [formData, setFormData] = useState({
    name: project?.name || '',
    description: project?.description || '',
    status: project?.status || 'planning',
    dueDate: project?.due_date ? new Date(project.due_date).toISOString().split('T')[0] : '',
    teamSize: project?.team_size || 1,
    vision: project?.vision || '',
    goals: project?.goals || ''
  });
  const [teamMembers, setTeamMembers] = useState<Array<Contact & {selected: boolean}>>([]);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!project;

  React.useEffect(() => {
    if (showTeamSelector) {
      loadTeamContacts();
    }
  }, [showTeamSelector]);

  const loadTeamContacts = async () => {
    if (!entity?.id) return;
    
    try {
      setLoadingContacts(true);
      const contacts = await getContacts(entity.id);
      // Filter to only team contacts
      const teamContacts = contacts.filter(contact => contact.type === 'team');
      
      setTeamMembers(teamContacts.map(contact => ({ 
        ...contact,
        selected: false
      })));
    } catch (error) {
      console.error('Failed to load team contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const toggleTeamMember = (id: string) => {
    setTeamMembers(prev => 
      prev.map(member => 
        member.id === id ? { ...member, selected: !member.selected } : member
      )
    );
    
    // Update team size based on selected members
    const selectedCount = teamMembers.filter(m => 
      m.id === id ? !m.selected : m.selected
    ).length;
    setFormData(prev => ({ ...prev, teamSize: Math.max(1, selectedCount) }));
  };

  const filteredTeamMembers = teamMembers.filter(member => 
    `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.title && member.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!user) {
      setError('Authentication error. Please try signing in again.');
      return;
    }
    
    if (!entity?.id) {
      setError('Entity not found. Please refresh and try again.');
      return;
    }

    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    try {
      setLoading(true);
      
      if (isEditing && project) {
        // Update existing project
        await updateProject(project.id, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          status: formData.status,
          due_date: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
          team_size: formData.teamSize,
          vision: formData.vision.trim(),
          goals: formData.goals.trim()
        });
      } else {
        // Create new project
        await createProject({
          name: formData.name.trim(),
          description: formData.description.trim(),
          status: formData.status,
          due_date: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
          team_size: formData.teamSize,
          vision: formData.vision.trim(),
          goals: formData.goals.trim(),
          entity_id: entity.id,
          created_by: user.id
        });
      }
      
      onSuccess();
    } catch (error) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} project:`, error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(`Failed to ${isEditing ? 'update' : 'create'} project. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">{isEditing ? 'Edit Project' : 'Create New Project'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter project name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="Describe your project"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="planning">Planning</option>
                <option value="in_progress">In Progress</option>
                <option value="review">Review</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <Users className="w-4 h-4 mr-1" />
              Team Size
              <button 
                type="button"
                onClick={() => setShowTeamSelector(true)}
                className="ml-2 text-xs text-blue-500 hover:text-blue-600 flex items-center"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                Select Members
              </button>
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={formData.teamSize}
              onChange={(e) => setFormData(prev => ({ ...prev, teamSize: parseInt(e.target.value) || 1 }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Vision
            </label>
            <textarea
              value={formData.vision}
              onChange={(e) => setFormData(prev => ({ ...prev, vision: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="What is the overall vision for this project?"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Goals
            </label>
            <textarea
              value={formData.goals}
              onChange={(e) => setFormData(prev => ({ ...prev, goals: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="What are the specific goals you want to achieve?"
              rows={2}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center"
            >
              {loading && (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              )}
              {!loading && (isEditing ? 'Update Project' : 'Create Project')}
            </button>
          </div>
        </form>
        
        {/* Team Member Selector Modal */}
        {showTeamSelector && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Select Team Members</h3>
                <button 
                  onClick={() => setShowTeamSelector(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4 relative">
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                />
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
              
              <div className="max-h-60 overflow-y-auto">
                {loadingContacts ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <p>No team members found</p>
                    <p className="text-sm mt-1">Add team contacts in the Contacts section first</p>
                  </div>
                ) : filteredTeamMembers.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <p>No matching team members found</p>
                  </div>
                ) : (
                  filteredTeamMembers.map(member => (
                    <div 
                      key={member.id}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-500 mr-3">
                          {`${member.first_name[0]}${member.last_name[0]}`.toUpperCase()}
                        </div>
                        <span>{`${member.first_name} ${member.last_name}`}</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={member.selected}
                          onChange={() => toggleTeamMember(member.id)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                      </label>
                    </div>
                  ))
                )}
              </div>
              
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowTeamSelector(false)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}